import { apiClient } from "./client";

export type AeroAxisRole = "section_shape" | "operating_condition" | "lifting_line_geometry" | "future_control";
export type AeroAxisSemantic = "generic" | "alpha" | "reynolds" | "mach";
export type AeroAxisDifficulty = "low" | "medium" | "high";

export interface AeroCampaignAxis {
  name: string;
  role: AeroAxisRole;
  lower: number;
  upper: number;
  transform: "linear" | "log10";
  semantic: AeroAxisSemantic;
  smoothness?: AeroAxisDifficulty;
  nonlinearity?: AeroAxisDifficulty;
  expected_correlation_length?: number;
}

export interface AeroCampaignPlanRequest {
  axes: AeroCampaignAxis[];
  outputs?: AeroCampaignOutput[];
  base_design_points: number;
  alpha_samples: number;
  failure_rate?: number;
}

export interface AeroCampaignOutput {
  name: string;
  target_normalized_error: number;
}

export interface AeroSolvePoint {
  point_id: string;
  alpha_sweep_id: string;
  coordinates: Array<[string, number]>;
  dataset_split?: string;
}

export interface AeroAlphaSweep {
  sweep_id: string;
  section_shape_coordinates: Array<[string, number]>;
  operating_condition_coordinates: Array<[string, number]>;
  reynolds: number | null;
  mach: number | null;
  alpha_values: number[];
  solve_point_ids: string[];
  dataset_split?: string;
}

export interface AeroCampaignPlanResponse {
  schema_version: "aero.section-campaign-plan.v1";
  plan: {
    sequence_name: string;
    requested_base_design_points: number;
    actual_alpha_sweep_count: number;
    training_alpha_sweep_count?: number;
    validation_alpha_sweep_count?: number;
    alpha_samples_per_sweep: number;
    unique_2d_solve_count: number;
    training_2d_solve_count?: number;
    validation_2d_solve_count?: number;
    solve_points: AeroSolvePoint[];
    alpha_sweeps: AeroAlphaSweep[];
    assumptions: string[];
  };
  training_budget: {
    recommended_initial_doe: number;
    adaptive_reserve: number;
    validation: number;
    total_low: number;
    total_likely: number;
    total_high: number;
    method: string;
    assumptions: string[];
    dimensionality_warning: string | null;
    requires_pilot_reestimate: boolean;
  };
}

export interface AeroWingBundleResponse {
  schema_version: "aero.geometry-bundle.v1";
  section_stack: Record<string, unknown>;
  shape_artifacts: Array<Record<string, unknown>>;
}

export interface AeroSectionSample {
  point_id: string;
  camber_ratio: number;
  camber_position_xc: number;
  thickness_ratio: number;
  alpha_deg: number;
  reynolds: number;
  mach: number;
  dataset_split?: string;
}

export interface AeroSectionSolveRow {
  point_id: string;
  shape_ref?: string;
  status: "done" | "error";
  error?: string;
  cached?: boolean;
  cache_key?: string;
  error_kind?: "solver" | "transport" | "unknown";
  retryable?: boolean;
  dataset_split?: string;
  shape_vector?: number[];
  solver_config?: Record<string, unknown>;
  memory_persisted?: boolean;
  publication_persisted?: boolean;
  source_evidence_persisted?: boolean;
  prediction_receipt?: {
    kind: "global-exact" | "scoped-exact" | "execution-cache-exact" | "solver-run" | string;
    exact: boolean;
    approximate?: boolean;
    scope: "private" | "shared";
    observation_key?: string;
    solver_backend: string;
    solver_build_id: string;
    method?: string;
    uncertainty?: Record<string, unknown>;
    neighbors?: Array<Record<string, unknown>>;
  };
  query?: Record<string, unknown>;
  prediction?: {
    cl: number;
    cd: number;
    cm: number;
    converged: boolean;
    convergence_quality: number;
    wake_momentum_thickness?: number | null;
    transition_xc?: number | null;
    separation_xc?: number | null;
    [key: string]: unknown;
  };
  raw?: Record<string, unknown>;
}

export interface AeroSectionSolveResponse {
  schema_version: "aero.section-samples.v1";
  solver: string;
  transport: "relay" | "ssh" | "local";
  requested: number;
  completed: number;
  failed: number;
  cache_hits?: number;
  solver_runs?: number;
  foil_memory?: {
    store_kind: string;
    requested_scope?: "private" | "shared";
    returned_scopes?: Array<"private" | "shared">;
    shared: boolean;
    exact_hits: number;
    execution_cache_hits?: number;
    persisted_observations?: number;
    publication_failures?: number;
    solver_runs: number;
    approximate_predictions?: boolean;
    approximate_hits?: number;
  };
  rows: AeroSectionSolveRow[];
}

export interface AeroWorkbenchCapabilities {
  section_solver: {
    name: string;
    configured: boolean;
    transport: "relay" | "ssh" | "local";
    relay_configured: boolean;
    relay_authenticated: boolean;
    max_points_per_request: number;
    outputs: string[];
  };
  foil_memory?: {
    configured: boolean;
    shared_store_configured: boolean;
    store_kind: string;
    exact_reuse: boolean;
    available_scopes: Array<"private" | "shared">;
    shared_publish_enabled: boolean;
    approximate_predictions: boolean;
    purpose: string;
  };
  rans_escalation: {
    configured: boolean;
    purpose: string;
  };
  quick_lifting_line?: {
    configured: boolean;
    wake_model: "relaxed-free-wake";
    free_wake: true;
    steady_state: true;
    history_dependent: false;
    max_horseshoes_per_request: number;
    default_wake_nodes_per_leg: number;
    section_model: string;
  };
  three_d_panel_solver_in_scope: boolean;
}

export interface AeroFoilMemoryContribution {
  contribution_id: string;
  observation_key: string;
  observation_id: string;
  policy_version: string;
  contributed_at: string;
  source_context: string | null;
  revoked_at: string | null;
  active: boolean;
}

export interface AeroFoilMemoryContributionsResponse {
  schema_version: "aero.foil-memory-contributions.v1";
  shared_store_configured: boolean;
  contributions: AeroFoilMemoryContribution[];
  next_cursor: string | null;
}

export interface AeroFoilMemoryRevocationResponse {
  schema_version: "aero.foil-memory-contribution-revocation.v1";
  contribution_id: string;
  found: boolean;
  revoked: boolean;
  shared_index_removed: boolean;
  observation_key: string | null;
  observation_id: string | null;
}

export type AeroVector3 = [number, number, number];

export interface AeroLiftingLineCondition {
  speed_mps: number;
  alpha_deg: number;
  beta_deg?: number;
  density_kg_m3?: number;
  dynamic_viscosity_pa_s?: number;
  speed_of_sound_mps?: number;
}

export interface AeroLiftingLineSettings {
  max_iterations?: number;
  relaxation?: number;
  relative_tolerance?: number;
  absolute_tolerance_m2_s?: number;
  vortex_core_radius_m?: number;
  wake_length_m?: number;
  wake_node_count_per_leg?: number;
  wake_max_iterations?: number;
  wake_relaxation?: number;
  wake_position_tolerance_m?: number;
  include_mirror_y?: boolean;
}

export interface AeroLiftingLineSolveRequest {
  wing: Record<string, unknown>;
  section_rows: AeroSectionSolveRow[];
  condition: AeroLiftingLineCondition;
  aircraft_model_instance_id?: string;
  input_slider_names?: string[];
  section_shape_axis_names?: string[];
  settings?: AeroLiftingLineSettings;
}

export interface AeroInverseLiftTargetPoint {
  eta: number;
  lift_per_span_n_m: number;
}

export type AeroInverseDesignObjective =
  | {
      kind: "match_lift_distribution";
      target_points: AeroInverseLiftTargetPoint[];
      normalize: boolean;
    }
  | {
      kind: "lift_to_drag";
      mode: "maximize" | "target";
      target_ratio?: number | null;
    };

export interface AeroInverseDesignVariable {
  kind: "station_twist_deg" | "station_chord_m";
  station_id: string;
  lower: number;
  upper: number;
}

export interface AeroInverseDesignOptimizer {
  max_evaluations?: number;
  initial_step_fraction?: number;
  min_step_fraction?: number;
}

export interface AeroLiftingLineInverseDesignRequest extends AeroLiftingLineSolveRequest {
  objective: AeroInverseDesignObjective;
  variables: AeroInverseDesignVariable[];
  optimizer?: AeroInverseDesignOptimizer;
}

export interface AeroLiftingLineSpanLoad {
  panel_id: string;
  surface_id: string;
  station_id: string;
  mirrored: boolean;
  quarter_chord_m: AeroVector3;
  span_width_m: number;
  circulation_m2_s: number;
  alpha_eff_deg: number;
  reynolds: number;
  mach: number;
  cl: number;
  cd: number;
  cm: number;
  lift_n: number;
  induced_drag_n: number;
  profile_drag_n: number;
  induced_velocity_mps: AeroVector3;
  section_converged: boolean;
  convergence_quality: number;
  prediction?: {
    converged?: boolean;
    convergence_quality?: number;
    metadata?: {
      outside_training_domain?: boolean;
      extrapolation_distance?: number;
      nearest_group_distance?: number;
      nearest_shape_distance?: number;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface AeroLiftingLineResult {
  span_loads: AeroLiftingLineSpanLoad[];
  total_lift_n: number;
  total_induced_drag_n: number;
  total_profile_drag_n: number;
  reference_area_m2: number;
  lift_coefficient: number;
  cd_induced: number;
  cd_profile: number;
  iterations: number;
  circulation_residual_m2_s: number;
  converged: boolean;
  valid: boolean;
  messages: string[];
  wake_direction: AeroVector3;
  lift_axis_body: AeroVector3;
  escalation?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface AeroLiftingLineHorseshoe {
  panel_id: string;
  surface_id: string;
  mirrored: boolean;
  bound_start_m: AeroVector3;
  bound_end_m: AeroVector3;
  wake_end_start_m: AeroVector3;
  wake_end_end_m: AeroVector3;
  circulation_m2_s: number;
}

export interface AeroLiftingLineWakeFilament {
  panel_id: string;
  surface_id: string;
  mirrored: boolean;
  circulation_m2_s: number;
  bound_start_m: AeroVector3;
  bound_end_m: AeroVector3;
  root_nodes_m: AeroVector3[];
  tip_nodes_m: AeroVector3[];
}

export interface AeroLiftingLineSolveResponse {
  schema_version: "aero.lifting-line-result.v2";
  prescribed_wake: false;
  wake_model: {
    kind: "relaxed-free-wake";
    free_wake: true;
    wake_length_m: number;
    node_count_per_leg: number;
    iteration_count: number;
    circulation_iteration_count: number;
    converged: boolean;
    position_residual_m: number;
    circulation_residual_m2_s: number;
    position_residual_history_m: number[];
    circulation_residual_history_m2_s: number[];
    [key: string]: unknown;
  };
  section_model: {
    kind: string;
    training_row_count?: number;
    trainable_row_count?: number;
    received_row_count?: number;
    excluded_validation_row_count?: number;
    [key: string]: unknown;
  };
  result: AeroLiftingLineResult;
  wake_filaments: AeroLiftingLineWakeFilament[];
  horseshoes: AeroLiftingLineHorseshoe[];
}

export interface AeroInverseDesignTrial {
  evaluation_index: number;
  variables: Record<string, number>;
  score: number;
  feasible: boolean;
  metrics: Record<string, number | boolean | string>;
}

export interface AeroLiftingLineInverseDesignResponse {
  schema_version: "aero.inverse-design-result.v1";
  objective: AeroInverseDesignObjective;
  best: {
    variables: Record<string, number>;
    score: number;
    feasible: boolean;
    metrics: Record<string, number | boolean | string>;
    wing: Record<string, unknown>;
    condition: AeroLiftingLineCondition;
    wake: AeroLiftingLineSolveResponse;
  };
  history: AeroInverseDesignTrial[];
  provenance: {
    kind: "aero.inverse-design-provenance.v1";
    algorithm: string;
    deterministic: boolean;
    random_seed: null;
    evaluation_budget: number;
    evaluation_count: number;
    termination: string;
    initial_step_fraction: number;
    final_step_fraction: number;
    variables: AeroInverseDesignVariable[];
    per_evaluation_work_units: number;
    aggregate_work_unit_limit: number;
    section_model_training_identity?: string;
    free_wake: boolean;
    section_shape_mutation: false;
    [key: string]: unknown;
  };
}

export interface ThreadAeroTarget {
  logical_id: string;
  name: string;
  metadata?: Record<string, unknown>;
}

export interface ThreadAeroContentIdentity {
  algorithm: "sha256";
  digest: string;
  canonicalizer: "rfc8785";
  canonicalizer_version: "1";
  byte_length: number;
  media_type: string;
  source_content_hash?: string | null;
}

export interface ThreadAeroOperationResult<TContent, TContentSummary = never> {
  operation_id: string;
  artifact_id: string;
  artifact_version_id: string;
  content_identity: ThreadAeroContentIdentity;
  replayed: boolean;
  content: TContent;
  content_summary?: TContentSummary;
  content_omitted?: boolean;
}

export interface ThreadAeroEnvelope<TContent, TContentSummary = never> {
  data: ThreadAeroOperationResult<TContent, TContentSummary>;
}

interface ThreadAeroWriteRequest {
  project_id: string;
  idempotency_key: string;
  target: ThreadAeroTarget;
}

export interface ThreadAeroGeometryRequest extends ThreadAeroWriteRequest {
  wing: Record<string, unknown>;
}

export interface ThreadAeroGeometryContent {
  schema_version: "aero.geometry-definition.v1";
  wing: Record<string, unknown>;
  geometry_bundle: AeroWingBundleResponse;
}

export interface ThreadAeroCampaignPlanRequest extends ThreadAeroWriteRequest {
  geometry_artifact_version_id: string;
  plan_request: AeroCampaignPlanRequest;
}

export interface ThreadAeroSectionBatchRequest extends ThreadAeroWriteRequest {
  geometry_artifact_version_id: string;
  campaign_plan_artifact_version_id: string;
  solve_request: {
    samples: AeroSectionSample[];
    ncrit: number;
    max_iterations: number;
    observation_scope?: "private" | "shared";
    allow_global_interpolation?: boolean;
  };
}

export interface ThreadAeroSectionModelContent extends Record<string, unknown> {
  format: "aero.aircraft-neural-section-state.v1" | "aero.campaign-gp-state.v1";
  training_identity: string;
  kind?: string;
  model_scope?: string;
  model_instance_id?: string;
  input_slider_names?: string[];
  uncertainty_kind?: string;
  counts: {
    training_row_count: number;
    fit_row_count: number;
    [key: string]: unknown;
  };
}

export interface ThreadAeroSectionModelRequest extends ThreadAeroWriteRequest {
  campaign_plan_artifact_version_id: string;
  evidence_artifact_version_ids: string[];
  max_fit_rows: number;
}

export interface ThreadAeroRelaxedWakeRunRequest extends ThreadAeroWriteRequest {
  geometry_artifact_version_id: string;
  section_model_artifact_version_id: string;
  condition: AeroLiftingLineCondition;
  settings?: AeroLiftingLineSettings;
}

export interface ThreadAeroInverseDesignRunRequest extends ThreadAeroWriteRequest {
  geometry_artifact_version_id: string;
  section_model_artifact_version_id: string;
  condition: AeroLiftingLineCondition;
  settings?: AeroLiftingLineSettings;
  objective: AeroInverseDesignObjective;
  variables: AeroInverseDesignVariable[];
  optimizer?: AeroInverseDesignOptimizer;
}

export type AeroThreeDReferenceKind = "flow360_rans" | "rans" | "panel" | "experiment";

export interface AeroThreeDReferenceSource {
  kind: AeroThreeDReferenceKind;
  source_id: string;
  run_id?: string;
  artifact_id?: string;
  version?: string;
  fidelity: string;
  solver_name?: string;
  solver_version?: string;
  metadata?: Record<string, unknown>;
}

export interface AeroThreeDCoefficientBasis {
  reference_area_m2: number;
  normalization: "q_inf_times_reference_area";
  lift_axis: "projected_body_plus_z_normal_to_freestream";
  drag_axis: "positive_freestream";
}

export interface AeroThreeDIntegratedReference {
  coefficient_basis: AeroThreeDCoefficientBasis;
  lift_coefficient?: number;
  drag_coefficient?: number;
  pitching_moment_coefficient?: number;
}

export interface AeroThreeDCoefficientBasisComparison {
  matches: boolean;
  reference: AeroThreeDCoefficientBasis | null;
  prediction: AeroThreeDCoefficientBasis & {
    lift_axis_body: AeroVector3;
    drag_axis_body: AeroVector3;
  };
  checks: {
    reference_area: {
      matches: boolean;
      prediction_m2: number;
      reference_m2: number | null;
      absolute_residual_m2: number | null;
      tolerance_m2: number | null;
    };
    normalization: boolean;
    lift_axis_convention: boolean;
    drag_axis_convention: boolean;
  };
}

export interface AeroThreeDSpanwiseReferencePoint {
  surface_id: string;
  span_coordinate_m: number;
  lift_n_per_m: number;
  drag_n_per_m?: number;
}

export interface AeroThreeDSpanwiseBasis {
  coordinate: "body_y" | "local_span";
  load_density: "per_body_y" | "per_local_span";
  lift_axis: "projected_body_plus_z_normal_to_freestream" | "body_plus_z";
  drag_axis: "positive_freestream" | "body_plus_x";
}

export interface AeroThreeDSpanwiseBasisComparison {
  matches: boolean;
  reference: AeroThreeDSpanwiseBasis | null;
  prediction: AeroThreeDSpanwiseBasis & {
    lift_axis_body: AeroVector3;
    drag_axis_body: AeroVector3;
  };
  checks: {
    coordinate: boolean;
    load_density: boolean;
    lift_axis_convention: boolean;
    drag_axis_convention: boolean;
  };
}

export interface AeroThreeDPredictionSpanwisePoint extends AeroThreeDSpanwiseReferencePoint {
  panel_id: string;
  body_y_width_m: number;
  drag_n_per_m: number;
}

export interface ThreadAeroThreeDReferenceRequest extends ThreadAeroWriteRequest {
  geometry_artifact_version_id: string;
  source: AeroThreeDReferenceSource;
  condition: AeroLiftingLineCondition;
  coordinate_frame?: "body-x-downstream-y-starboard-z-up";
  integrated?: AeroThreeDIntegratedReference;
  spanwise?: AeroThreeDSpanwiseReferencePoint[];
  spanwise_basis?: AeroThreeDSpanwiseBasis;
}

export interface AeroThreeDReferenceContent {
  schema_version: "aero.three-dimensional-reference.v1";
  geometry_artifact_version_id: string;
  source: AeroThreeDReferenceSource;
  condition: AeroLiftingLineCondition;
  coordinate_frame: "body-x-downstream-y-starboard-z-up";
  integrated?: AeroThreeDIntegratedReference | null;
  spanwise?: AeroThreeDSpanwiseReferencePoint[] | null;
  spanwise_basis?: AeroThreeDSpanwiseBasis | null;
  evidence_claim: {
    thread_executed_source_solver: false;
    values_supplied_by_importer: true;
    reference_values_generated_by_thread: false;
    source_identity_verification: "declared-provenance-only";
  };
}

export interface ThreadAeroThreeDComparisonRequest extends ThreadAeroWriteRequest {
  prediction_artifact_version_id: string;
  reference_artifact_version_id: string;
}

export interface AeroThreeDResidual {
  prediction: number;
  reference: number;
  residual: number;
  absolute_residual: number;
  relative_residual: number | null;
  unit: string;
}

export interface AeroThreeDComparisonContent {
  schema_version: "aero.three-dimensional-comparison.v1";
  comparison_kind: "stacked-sectional-relaxed-wake-vs-imported-3d";
  comparable: boolean;
  geometry_artifact_version_id: string;
  prediction: {
    artifact_version_id: string;
    schema_version: string;
    model_kind: string;
    validity: {
      result_valid: boolean;
      result_converged: boolean;
      wake_converged: boolean;
    };
  };
  comparison_gates: {
    condition_within_tolerance: boolean;
    result_valid: boolean;
    result_converged: boolean;
    wake_converged: boolean;
    metric_available: boolean;
  };
  reference: {
    artifact_version_id: string;
    schema_version: string;
    source: AeroThreeDReferenceSource;
  };
  condition_match: {
    within_tolerance: boolean;
    fields: Record<string, unknown>;
    tolerance_policy: string;
  };
  integrated: {
    available: boolean;
    metrics: Partial<Record<"lift_coefficient" | "drag_coefficient" | "lift_to_drag_ratio", AeroThreeDResidual>>;
    coefficient_basis: AeroThreeDCoefficientBasisComparison | null;
    unavailable_reason: string | null;
  };
  spanwise: {
    available: boolean;
    coordinate: "body-y";
    coordinate_unit: "m";
    load_unit: "N/m";
    load_basis: "per-body-y";
    spanwise_basis: AeroThreeDSpanwiseBasisComparison | null;
    alignment_method: string;
    extrapolation: false;
    prediction_points: AeroThreeDPredictionSpanwisePoint[];
    reference_points: AeroThreeDSpanwiseReferencePoint[];
    aligned_points: Array<{
      surface_id: string;
      span_coordinate_m: number;
      lift: AeroThreeDResidual;
      drag?: AeroThreeDResidual;
    }>;
    metrics: Partial<Record<"lift_n_per_m" | "drag_n_per_m", {
      point_count: number;
      bias: number;
      rmse: number;
      normalized_rmse: number | null;
      max_absolute_residual: number;
    }>>;
    coverage: {
      reference_point_count: number;
      aligned_point_count: number;
      fraction: number;
    };
    unavailable_reason: string | null;
  };
  warnings: string[];
}

async function postThreadAero<TContent, TContentSummary = never>(
  path: string,
  body: ThreadAeroWriteRequest,
  signal?: AbortSignal,
): Promise<ThreadAeroOperationResult<TContent, TContentSummary>> {
  const response = await apiClient<ThreadAeroEnvelope<TContent, TContentSummary>>(path, {
    method: "POST",
    body,
    signal,
  });
  return response.data;
}

export const saveThreadAeroGeometry = (request: ThreadAeroGeometryRequest, signal?: AbortSignal) =>
  postThreadAero<ThreadAeroGeometryContent>("/thread/v1/aero/geometry", request, signal);

export const createThreadAeroCampaignPlan = (request: ThreadAeroCampaignPlanRequest, signal?: AbortSignal) =>
  postThreadAero<AeroCampaignPlanResponse>("/thread/v1/aero/campaign-plans", request, signal);

export const solveThreadAeroSectionBatch = (request: ThreadAeroSectionBatchRequest, signal?: AbortSignal) =>
  postThreadAero<AeroSectionSolveResponse>("/thread/v1/aero/section-batches", request, signal);

export const fitThreadAeroSectionModel = (request: ThreadAeroSectionModelRequest, signal?: AbortSignal) =>
  postThreadAero<null, ThreadAeroSectionModelContent>("/thread/v1/aero/section-models", request, signal);

export const runThreadAeroRelaxedWake = (request: ThreadAeroRelaxedWakeRunRequest, signal?: AbortSignal) =>
  postThreadAero<AeroLiftingLineSolveResponse>("/thread/v1/aero/relaxed-wake-runs", request, signal);

export const runThreadAeroInverseDesign = (request: ThreadAeroInverseDesignRunRequest, signal?: AbortSignal) =>
  postThreadAero<AeroLiftingLineInverseDesignResponse>("/thread/v1/aero/inverse-design-runs", request, signal);

export const importThreadAeroThreeDReference = (request: ThreadAeroThreeDReferenceRequest, signal?: AbortSignal) =>
  postThreadAero<AeroThreeDReferenceContent>("/thread/v1/aero/three-d-references", request, signal);

export const compareThreadAeroThreeD = (request: ThreadAeroThreeDComparisonRequest, signal?: AbortSignal) =>
  postThreadAero<AeroThreeDComparisonContent>("/thread/v1/aero/three-d-comparisons", request, signal);

export const aeroWorkbenchApi = {
  capabilities: (signal?: AbortSignal) =>
    apiClient<AeroWorkbenchCapabilities>("/aero-workbench/capabilities", { signal }),
  wingBundle: (wing: Record<string, unknown>, signal?: AbortSignal) =>
    apiClient<AeroWingBundleResponse>("/aero-workbench/geometry/wing-bundle", {
      method: "POST",
      body: { wing },
      signal,
    }),
  campaignPlan: (request: AeroCampaignPlanRequest, signal?: AbortSignal) =>
    apiClient<AeroCampaignPlanResponse>("/aero-workbench/campaign/plan", {
      method: "POST",
      body: request,
      signal,
    }),
  solveSections: (
    samples: AeroSectionSample[],
    maxIterations = 120,
    signal?: AbortSignal,
    observationScope: "private" | "shared" = "private",
    allowGlobalInterpolation = false,
  ) =>
    apiClient<AeroSectionSolveResponse>("/aero-workbench/sections/solve", {
      method: "POST",
      body: {
        samples,
        max_iterations: maxIterations,
        observation_scope: observationScope,
        allow_global_interpolation: allowGlobalInterpolation,
      },
      signal,
    }),
  solveLiftingLine: (request: AeroLiftingLineSolveRequest, signal?: AbortSignal) =>
    apiClient<AeroLiftingLineSolveResponse>("/aero-workbench/lifting-line/solve", {
      method: "POST",
      body: request,
      signal,
    }),
  inverseDesignLiftingLine: (
    request: AeroLiftingLineInverseDesignRequest,
    signal?: AbortSignal,
  ) =>
    apiClient<AeroLiftingLineInverseDesignResponse>(
      "/aero-workbench/lifting-line/inverse-design",
      { method: "POST", body: request, signal },
    ),
  foilMemoryContributions: (
    includeRevoked = false,
    signal?: AbortSignal,
    afterContributionId?: string,
  ) =>
    apiClient<AeroFoilMemoryContributionsResponse>(
      `/aero-workbench/foil-memory/contributions?include_revoked=${includeRevoked ? "true" : "false"}${afterContributionId ? `&after_contribution_id=${encodeURIComponent(afterContributionId)}` : ""}`,
      { signal },
    ),
  revokeFoilMemoryContribution: (contributionId: string, signal?: AbortSignal) =>
    apiClient<AeroFoilMemoryRevocationResponse>(
      `/aero-workbench/foil-memory/contributions/${encodeURIComponent(contributionId)}/revoke`,
      { method: "POST", signal },
    ),
  saveThreadGeometry: saveThreadAeroGeometry,
  createThreadCampaignPlan: createThreadAeroCampaignPlan,
  solveThreadSectionBatch: solveThreadAeroSectionBatch,
  fitThreadSectionModel: fitThreadAeroSectionModel,
  runThreadRelaxedWake: runThreadAeroRelaxedWake,
  runThreadInverseDesign: runThreadAeroInverseDesign,
  importThreadThreeDReference: importThreadAeroThreeDReference,
  compareThreadThreeD: compareThreadAeroThreeD,
};
